// @catalog F.030
// BottomMarginProductsTable — productos con margen crítico (estilo handoff).
// Filtro por nombre + columnas sortables.
import { useMemo } from "react";
import { Alert, Box, Stack } from "@mui/material";
import MarginsService, {
  type TopMarginProduct,
} from "../../services/margins.service";
import {
  TableShell,
  HeaderCell,
  BodyRow,
  BodyCell,
  ProductCell,
  Tag,
  type TagTone,
} from "../analytics/TableCells";
import {
  SortableHeaderCell,
  TableFilterInput,
  useTableControls,
} from "../analytics/TableControls";

interface BottomMarginProductsTableProps {
  products: TopMarginProduct[];
  // Click handler opcional — recibe productId (parseado a int) + nombre.
  onProductClick?: (productId: number, productName: string) => void;
}

type Severity = "critico" | "bajo" | "atencion";

function severityFor(realMarginPct: number): Severity {
  if (realMarginPct < 15) return "critico";
  if (realMarginPct < 22) return "bajo";
  return "atencion";
}

function severityLabel(sev: Severity): { label: string; tone: TagTone } {
  if (sev === "critico") return { label: "Crítico", tone: "alert" };
  if (sev === "bajo") return { label: "Bajo", tone: "warn" };
  return { label: "Atención", tone: "neutral" };
}

function suggestedAction(product: TopMarginProduct): string {
  const hasCommissions =
    product.totalCommissions !== undefined && product.totalCommissions > 0;
  const hasDiscounts =
    product.totalDiscounts !== undefined && product.totalDiscounts > 0;
  if (hasCommissions && hasDiscounts) return "Revisar comisión + descuento";
  if (hasCommissions) return "Revisar comisión categoría";
  if (hasDiscounts) return "Limitar descuentos máx.";
  return "Revisar costo / proveedor";
}

function splitNameSku(fullName: string): { sku: string | null; name: string } {
  const trimmed = fullName.trim();
  const firstSpace = trimmed.indexOf(" ");
  if (firstSpace === -1) return { sku: null, name: trimmed };
  const possibleSku = trimmed.slice(0, firstSpace);
  if (
    possibleSku.length >= 3 &&
    possibleSku.length <= 20 &&
    /[\d-]/.test(possibleSku)
  ) {
    return { sku: possibleSku, name: trimmed.slice(firstSpace + 1) };
  }
  return { sku: null, name: trimmed };
}

export function BottomMarginProductsTable({
  products,
  onProductClick,
}: BottomMarginProductsTableProps): JSX.Element {
  const filterAccessor = useMemo(
    () => (p: TopMarginProduct) =>
      MarginsService.extractProductName(p.productName),
    [],
  );
  const sortAccessors = useMemo(
    () => ({
      severity: (p: TopMarginProduct) =>
        p.realMarginPercentage ?? p.marginPercentage,
      product: (p: TopMarginProduct) =>
        MarginsService.extractProductName(p.productName),
      nominal: (p: TopMarginProduct) => p.marginPercentage,
      real: (p: TopMarginProduct) =>
        p.realMarginPercentage ?? p.marginPercentage,
      erosion: (p: TopMarginProduct) =>
        p.marginPercentage - (p.realMarginPercentage ?? p.marginPercentage),
      discounts: (p: TopMarginProduct) => p.totalDiscounts ?? 0,
      commissions: (p: TopMarginProduct) => p.totalCommissions ?? 0,
    }),
    [],
  );

  // Default sort = severidad ascendente (los más críticos primero).
  const { filter, setFilter, sort, toggleSort, processedData } =
    useTableControls<TopMarginProduct>({
      data: products ?? [],
      filterAccessor,
      sortAccessors,
      defaultSort: { key: "severity", dir: "asc" },
    });

  if (!products || products.length === 0) {
    return (
      <Alert severity="success">
        No hay productos críticos en el período actual.
      </Alert>
    );
  }

  return (
    <Stack spacing={1.5}>
      <TableFilterInput
        value={filter}
        onChange={setFilter}
        placeholder="Buscar producto…"
        helper={
          filter
            ? `${processedData.length} de ${products.length}`
            : `${products.length} productos`
        }
      />
      {processedData.length === 0 ? (
        <Alert severity="info">Sin resultados para "{filter}".</Alert>
      ) : (
        <TableShell maxHeight={500}>
          <thead>
            <tr>
              <SortableHeaderCell
                sortKey="severity"
                currentSort={sort}
                onSort={toggleSort}
                width={110}
              >
                Severidad
              </SortableHeaderCell>
              <SortableHeaderCell
                sortKey="product"
                currentSort={sort}
                onSort={toggleSort}
              >
                Producto
              </SortableHeaderCell>
              <SortableHeaderCell
                sortKey="nominal"
                currentSort={sort}
                onSort={toggleSort}
                align="right"
              >
                Margen nominal
              </SortableHeaderCell>
              <SortableHeaderCell
                sortKey="real"
                currentSort={sort}
                onSort={toggleSort}
                align="right"
              >
                Margen real
              </SortableHeaderCell>
              <SortableHeaderCell
                sortKey="erosion"
                currentSort={sort}
                onSort={toggleSort}
                align="right"
              >
                Erosión
              </SortableHeaderCell>
              <SortableHeaderCell
                sortKey="discounts"
                currentSort={sort}
                onSort={toggleSort}
                align="right"
              >
                Descuentos
              </SortableHeaderCell>
              <SortableHeaderCell
                sortKey="commissions"
                currentSort={sort}
                onSort={toggleSort}
                align="right"
              >
                Comisiones
              </SortableHeaderCell>
              <HeaderCell>Acción sugerida</HeaderCell>
            </tr>
          </thead>
          <tbody>
            {processedData.map((product) => {
              const fullName = MarginsService.extractProductName(
                product.productName,
              );
              const { sku, name } = splitNameSku(fullName);
              const nominal = product.marginPercentage;
              const real =
                product.realMarginPercentage ?? product.marginPercentage;
              const erosionPp = nominal - real;
              const sev = severityFor(real);
              const sevTag = severityLabel(sev);
              const realTone: TagTone =
                sev === "critico"
                  ? "alert"
                  : sev === "bajo"
                    ? "warn"
                    : "neutral";
              const parsedId = parseInt(String(product.productId), 10);
              const handleClick =
                onProductClick && Number.isFinite(parsedId) && parsedId > 0
                  ? () => onProductClick(parsedId, fullName)
                  : undefined;
              return (
                <BodyRow
                  key={`${product.productId}-${product.storeName}`}
                  {...(handleClick && { onClick: handleClick })}
                >
                  <BodyCell>
                    <Tag label={sevTag.label} tone={sevTag.tone} />
                  </BodyCell>
                  <BodyCell>
                    <ProductCell
                      sku={sku}
                      name={name}
                      subtitle={product.storeName}
                    />
                  </BodyCell>
                  <BodyCell align="right" muted>
                    {nominal.toFixed(1)}%
                  </BodyCell>
                  <BodyCell align="right">
                    <Tag label={`${real.toFixed(1)}%`} tone={realTone} />
                  </BodyCell>
                  <BodyCell align="right">
                    <Box
                      component="span"
                      sx={{
                        color: erosionPp > 0 ? "var(--neg)" : "var(--ink-3)",
                        fontWeight: 500,
                        fontVariantNumeric: "tabular-nums",
                      }}
                    >
                      {erosionPp > 0 ? `−${erosionPp.toFixed(1)} pp` : "—"}
                    </Box>
                  </BodyCell>
                  <BodyCell align="right" muted>
                    {product.totalDiscounts !== undefined &&
                    product.totalDiscounts > 0
                      ? MarginsService.formatCurrency(product.totalDiscounts)
                      : "—"}
                  </BodyCell>
                  <BodyCell align="right" muted>
                    {product.totalCommissions !== undefined &&
                    product.totalCommissions > 0
                      ? MarginsService.formatCurrency(product.totalCommissions)
                      : "—"}
                  </BodyCell>
                  <BodyCell>
                    <Box
                      component="span"
                      sx={{ color: "var(--ink-3)", fontSize: "12px" }}
                    >
                      {suggestedAction(product)}
                    </Box>
                  </BodyCell>
                </BodyRow>
              );
            })}
          </tbody>
        </TableShell>
      )}
    </Stack>
  );
}
